import type { StudentPracticeQuizListItem } from "./api";

export type GlobalPracticeMetadata = {
  title: string;
  level?: string | null;
  subject?: string | null;
  course_level?: string | null;
  paper?: string | null;
  topic?: string | null;
};

export type GlobalPracticeQuiz = StudentPracticeQuizListItem & { scope: "global" };
export type PracticeFolderKind = "course_level" | "paper" | "topic";
export type PracticeFolder<T extends GlobalPracticeMetadata = GlobalPracticeQuiz> = {
  key: string;
  title: string;
  kind: PracticeFolderKind;
  folders: PracticeFolder<T>[];
  quizzes: T[];
};
export type PracticeCollection<T extends GlobalPracticeMetadata = GlobalPracticeQuiz> = {
  key: string;
  title: string;
  level: string;
  subject: string;
  folders: PracticeFolder<T>[];
};

export const slugifyPracticeLibraryPart = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
export const collectionLabel = (level: string, subject: string) => `${level} ${subject === "Mathematics" ? "Maths" : subject}`.trim();

const folderKey = (kind: PracticeFolderKind, title: string) => `${kind}-${slugifyPracticeLibraryPart(title)}`;
const sortFolders = <T extends GlobalPracticeMetadata>(folders: PracticeFolder<T>[]): PracticeFolder<T>[] => folders
  .map((folder) => ({ ...folder, folders: sortFolders(folder.folders), quizzes: [...folder.quizzes].sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true })) }))
  .sort((a, b) => a.title.localeCompare(b.title));

/** Builds a hierarchy from metadata, omitting optional dimensions when NULL. */
export function groupGlobalPracticeRows<T extends GlobalPracticeMetadata>(rows: T[]): PracticeCollection<T>[] {
  const collections = new Map<string, PracticeCollection<T>>();
  rows.forEach((quiz) => {
    const level = String(quiz.level || "Other").trim() || "Other";
    const subject = String(quiz.subject || "Practice").trim() || "Practice";
    const topic = String(quiz.topic || "General").trim() || "General";
    const key = `${slugifyPracticeLibraryPart(level)}-${slugifyPracticeLibraryPart(subject)}`;
    let collection = collections.get(key);
    if (!collection) { collection = { key, title: collectionLabel(level, subject), level, subject, folders: [] }; collections.set(key, collection); }
    const courseLevel = String(quiz.course_level || "").trim();
    const paper = String(quiz.paper || "").trim();
    const dimensions: Array<{ kind: PracticeFolderKind; title: string }> = [
      ...(courseLevel ? [{ kind: "course_level" as const, title: courseLevel }] : []),
      ...(paper ? [{ kind: "paper" as const, title: paper }] : []),
      { kind: "topic", title: topic },
    ];
    let folders = collection.folders;
    dimensions.forEach(({ kind, title }, index) => {
      const currentKey = folderKey(kind, title);
      let folder = folders.find((item) => item.key === currentKey);
      if (!folder) { folder = { key: currentKey, title, kind, folders: [], quizzes: [] }; folders.push(folder); }
      if (index === dimensions.length - 1) folder.quizzes.push(quiz);
      folders = folder.folders;
    });
  });
  return Array.from(collections.values()).map((collection) => ({ ...collection, folders: sortFolders(collection.folders) })).sort((a, b) => a.title.localeCompare(b.title));
}

export function groupGlobalPracticeQuizzes(rows: StudentPracticeQuizListItem[]): PracticeCollection[] {
  return groupGlobalPracticeRows(rows.filter((row): row is GlobalPracticeQuiz => row.scope === "global"));
}

export function practiceFolderQuizCount<T extends GlobalPracticeMetadata>(folder: PracticeFolder<T>): number {
  return folder.quizzes.length + folder.folders.reduce((total, child) => total + practiceFolderQuizCount(child), 0);
}
